import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import { buyerOrdersService } from '../../../services/buyerService';
import { format } from 'date-fns';

const PurchaseTrendsChart = ({ language, startDate, endDate }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const content = {
    en: {
      title: 'Purchase Trends Over Time',
      orders: 'Orders',
      spending: 'Spending (USD)',
      noData: 'No purchase data available for this period'
    },
    zh: {
      title: '采购趋势',
      orders: '订单',
      spending: '支出（美元）',
      noData: '此期间没有采购数据'
    }
  };

  const t = content?.[language];

  useEffect(() => {
    loadTrendsData();
  }, [startDate, endDate]);

  const loadTrendsData = async () => {
    try {
      setLoading(true);
      const orders = await buyerOrdersService?.getAll();
      
      // Filter by date range
      const filtered = orders?.filter(order => {
        const orderDate = new Date(order?.createdAt);
        return orderDate >= startDate && orderDate <= endDate;
      });

      // Group by date
      const grouped = filtered?.reduce((acc, order) => {
        const date = format(new Date(order?.createdAt), 'MMM dd');
        if (!acc?.[date]) {
          acc[date] = { date, orders: 0, spending: 0 };
        }
        acc[date].orders += 1;
        acc[date].spending += (order?.quantityKg || 0) * (order?.bean?.priceUsdPerKg || 0);
        return acc;
      }, {});

      const chartData = Object.values(grouped);
      setData(chartData);
    } catch (error) {
      console.error('Failed to load trends:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-center h-64">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-2 mb-6">
        <Icon name="TrendingUp" size={20} className="text-primary" />
        <h3 className="text-lg font-heading font-semibold text-foreground">{t?.title}</h3>
      </div>
      {data?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="BarChart3" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">{t?.noData}</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="date" stroke="var(--color-muted-foreground)" />
            <YAxis yAxisId="left" stroke="var(--color-primary)" />
            <YAxis yAxisId="right" orientation="right" stroke="var(--color-secondary)" />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--color-card)',
                border: '1px solid var(--color-border)',
                borderRadius: '8px'
              }}
            />
            <Legend />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="orders"
              stroke="var(--color-primary)"
              strokeWidth={2}
              name={t?.orders}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="spending"
              stroke="var(--color-secondary)"
              strokeWidth={2}
              name={t?.spending}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default PurchaseTrendsChart;